import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, getErrorMessage } from "@/lib/api";

function Frame({ title, children, testid }) {
  return (
    <div data-testid={testid} className="min-h-screen ss-gradient-warm flex items-center justify-center px-4 py-12">
      <div className="ss-card w-full max-w-md p-8 sm:p-10">
        <Link to="/" data-testid="auth-logo" className="font-heading text-[1.75rem] md:text-3xl text-cocoa tracking-tight">Sugar<span className="italic font-medium text-mauve">Sense</span></Link>
        <h1 className="font-heading text-3xl text-cocoa mt-6">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export const landingFor = (u) => (u?.role === "admin" ? "/admin-ss/overview" : "/dashboard");

export function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav(landingFor(user), { replace: true }); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) nav(landingFor(r.user));
    else setErr(r.error || "Login failed");
  };
  return (
    <Frame title="Welcome back." testid="login-page">
      <form onSubmit={submit} className="mt-6 space-y-4">
        <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email"
          className="w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none" />
        <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="Password" minLength={8}
          className="w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none" />
        {err && <div data-testid="login-err" className="text-rose text-sm">{err}</div>}
        <button data-testid="login-submit" type="submit" disabled={busy} className="ss-pill ss-pill-primary w-full h-12 disabled:opacity-50">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="text-sm text-cocoa/60 flex justify-between pt-2">
          <Link to="/forgot-password" className="hover:text-cocoa">Forgot password?</Link>
          <Link to="/register" className="hover:text-cocoa">Create account</Link>
        </div>
      </form>
    </Frame>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const ambassadorToken = (params.get("ambassador") || "").trim();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);
  const [inviteErr, setInviteErr] = useState("");

  useEffect(() => {
    if (!ambassadorToken) return;
    api.get(`/ambassador-invites/${encodeURIComponent(ambassadorToken)}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setInviteErr(e?.response?.data?.detail || "Invite is no longer valid"));
  }, [ambassadorToken]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    const payload = { email: email.trim(), password, name };
    if (ambassadorToken && invite?.valid) payload.ambassador_token = ambassadorToken;
    const r = await register(payload);
    setBusy(false);
    if (r.ok) nav("/onboarding");
    else setErr(r.error || "Sign up failed");
  };
  return (
    <Frame title="Begin privately." testid="register-page">
      {ambassadorToken && invite?.valid && (
        <div data-testid="ambassador-banner" className="mt-4 ss-card-premium p-4 bg-mauve/10 border border-mauve/30">
          <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">Ambassador invite</p>
          <p className="mt-1 text-cocoa font-heading text-lg leading-snug">
            You've been invited — full Luxe access, free forever.
          </p>
          {invite.label && <p className="mt-1 text-xs text-cocoa/60 italic">via {invite.label}</p>}
        </div>
      )}
      {ambassadorToken && inviteErr && (
        <div data-testid="ambassador-banner-err" className="mt-4 ss-card p-3 text-sm text-rose bg-rose/10">
          {inviteErr} — you can still create a free account below.
        </div>
      )}
      <p className="text-cocoa/70 text-sm mt-2">Free to start. Three insights a month. Upgrade anytime.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your first name (or alias)" minLength={1} maxLength={40}
          className="w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none" />
        <input data-testid="register-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email"
          className="w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none" />
        <input data-testid="register-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={12} placeholder="Password (12+ chars)"
          className="w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none" />
        {err && <div data-testid="register-err" className="text-rose text-sm">{err}</div>}
        <button data-testid="register-submit" type="submit" disabled={busy} className="ss-pill ss-pill-gold w-full h-12 disabled:opacity-50">
          {busy ? "Creating…" : (invite?.valid ? "Claim your invite" : "Create account")}
        </button>
        <p className="text-xs text-cocoa/50">By signing up you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
      </form>
    </Frame>
  );
}

export function SetPassword() {
  const params = new URLSearchParams(window.location.search);
  const token = (params.get("token") || "").trim();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [linkErr, setLinkErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setLinkErr("This link is missing its token. Ask your contact for a fresh one."); return; }
    api.get(`/auth/set-password/${encodeURIComponent(token)}`).then((r) => setInfo(r.data)).catch((e) => setLinkErr(getErrorMessage(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (password.length < 12) return setErr("Password must be at least 12 characters.");
    if ([/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length < 3) return setErr("Use at least 3 of: lowercase, uppercase, digit, symbol.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, new_password: password }); setDone(true); setTimeout(() => nav("/login"), 1500); }
    catch (e2) { setErr(getErrorMessage(e2)); } finally { setBusy(false); }
  };
  const field = "w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none";
  return (
    <Frame title={done ? "You're in." : "Welcome. Set your password."} testid="set-password-page">
      {linkErr ? <p data-testid="set-password-link-err" className="mt-4 text-rose text-sm">{linkErr}</p>
        : done ? <p data-testid="set-password-done" className="mt-4 text-cocoa/70">Your account is active. Taking you to sign in…</p>
        : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <p className="text-cocoa/70 text-sm" data-testid="set-password-intro">{info ? <>Your account <strong>{info.email}</strong> is ready — choose a password to activate it.</> : "Checking your link…"}</p>
            <input data-testid="set-password-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={12} placeholder="Password (12+ chars)" className={field} />
            <input data-testid="set-password-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" required minLength={12} placeholder="Confirm password" className={field} />
            {err && <div data-testid="set-password-err" className="text-rose text-sm">{err}</div>}
            <button data-testid="set-password-submit" type="submit" disabled={busy || !info} className="ss-pill ss-pill-gold w-full h-12 disabled:opacity-50">{busy ? "Activating…" : "Activate my account"}</button>
            <p className="text-xs text-cocoa/50">By activating you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
          </form>
        )}
    </Frame>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch { setSent(true); }
  };
  return (
    <Frame title="Reset password." testid="forgot-page">
      {sent ? (
        <p data-testid="forgot-sent" className="mt-4 text-cocoa/70">If that email is registered, you'll receive a reset link shortly.</p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email"
            className="w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none" />
          <button data-testid="forgot-submit" type="submit" className="ss-pill ss-pill-primary w-full h-12">Send reset link</button>
        </form>
      )}
    </Frame>
  );
}
